import { useMemo } from 'react';

export const WINDOW_STATUS = {
  CURRENT: 'current',
  FUTURE: 'future',
};

export const CHANNEL_RIGHTS = {
  HAS_RIGHTS_ACTIVE: 'has_rights_active',
  HAS_RIGHTS_FUTURE: 'has_rights_future',
  NO_RIGHTS: 'no_rights',
};

/**
 * Clasifica una ventana de tiempo según la fecha actual.
 * @param {string} dateBegin - ISO8601
 * @param {string} dateEnd   - ISO8601
 * @returns {'current' | 'future' | null}
 */
/**
 * Exportado para uso en useAlternateTimers.
 * Normaliza fechas del API que pueden venir sin separador T ni sufijo Z
 * (ej: "2026-03-31 11:00:00") a ISO 8601 con Z para que new Date()
 * las interprete siempre como UTC, no como hora local del browser.
 */
export const toUTCDate = dateStr => {
  if (!dateStr) return NaN;
  // Si ya tiene T o Z es ISO 8601 válido
  if (dateStr.includes('T') || dateStr.includes('Z')) return new Date(dateStr).getTime();
  // Formato "YYYY-MM-DD HH:MM:SS" → reemplazar espacio por T y agregar Z
  return new Date(dateStr.replace(' ', 'T')).getTime();
};

const classifyWindow = (dateBegin, dateEnd) => {
  const now = Date.now();
  const begin = toUTCDate(dateBegin);
  const end = toUTCDate(dateEnd);

  if (isNaN(begin) || isNaN(end)) return null;
  if (now >= begin && now <= end) return WINDOW_STATUS.CURRENT;
  if (now < begin) return WINDOW_STATUS.FUTURE;
  return null;
};

/**
 * Interpreta el atributo `media` de un canal.
 * - Objeto con datos → HAS_RIGHTS_ACTIVE
 * - {} (objeto vacío)  → HAS_RIGHTS_FUTURE
 * - null               → NO_RIGHTS
 */
const classifyMedia = media => {
  if (media === null) return CHANNEL_RIGHTS.NO_RIGHTS;
  if (typeof media === 'object' && Object.keys(media).length === 0) return CHANNEL_RIGHTS.HAS_RIGHTS_FUTURE;
  return CHANNEL_RIGHTS.HAS_RIGHTS_ACTIVE;
};

/**
 * Recorre TODOS los canales alternos en orden y devuelve el primero accionable.
 *
 * El spec (WEBCL-2943) indica que tanto media={} como media=null deben resultar en
 * "continuar evaluación de canales". Por eso no se hace early-return en esos casos:
 * se escanea el arreglo completo antes de decidir.
 *
 * Prioridad (respetando el orden del arreglo):
 *   1. Primer HAS_RIGHTS_ACTIVE + CURRENT  → FLUJO A (reproducir)
 *   2. Primer HAS_RIGHTS_FUTURE + FUTURE   → FLUJO B (timer a dateBegin)
 *   3. Si TODOS son NO_RIGHTS              → FLUJO C/D (oferta comercial)
 *
 * @param {Array}  channels    - data.channels[] del API v2
 * @param {string} scheduledAt - ISO8601 fecha/hora de inicio del partido
 * @returns {{
 *   channel: object | null,
 *   rights: string | null,
 *   windowStatus: string | null,
 *   showCommercialOffer: boolean,
 *   allChannels: Array,
 * }}
 */
const resolveActiveChannel = (channels, scheduledAt) => {
  let firstRightsFuture = null;
  let firstNoRights = null;
  let allNoRights = true;

  for (const channel of channels) {
    const event = channel.events?.[0];
    if (!event) continue;

    const rights = classifyMedia(channel.media);
    const windowStatus = classifyWindow(event.dateBegin, event.dateEnd);

    // Primer canal con derechos y ventana vigente → FLUJO A. Gana sin importar lo que haya antes.
    if (rights === CHANNEL_RIGHTS.HAS_RIGHTS_ACTIVE && windowStatus === WINDOW_STATUS.CURRENT) {
      return {
        channel,
        rights,
        windowStatus,
        showCommercialOffer: false,
        allChannels: channels,
      };
    }

    // Guardar el primer canal con derechos y ventana futura (continuar evaluando por si hay uno activo después)
    if (rights === CHANNEL_RIGHTS.HAS_RIGHTS_FUTURE && windowStatus === WINDOW_STATUS.FUTURE) {
      if (!firstRightsFuture) firstRightsFuture = { channel, rights, windowStatus };
      allNoRights = false;
      continue;
    }

    if (rights === CHANNEL_RIGHTS.NO_RIGHTS) {
      if (!firstNoRights) {
        // Guardar el primero que encontremos
        firstNoRights = { channel, windowStatus };
      } else if (
        windowStatus === WINDOW_STATUS.CURRENT &&
        firstNoRights.windowStatus !== WINDOW_STATUS.CURRENT
      ) {
        // Preferir CURRENT sobre FUTURE: si ya teníamos un FUTURE y aparece un CURRENT, lo reemplazamos
        firstNoRights = { channel, windowStatus };
      }
    }
    // HAS_RIGHTS_ACTIVE con ventana no vigente (expirada): no aporta reproducción
    // ni bloquea la oferta comercial — se ignora para el cálculo de allNoRights
  }

  // No había ningún ACTIVE+CURRENT. Usar el primer canal con derechos y ventana futura (FLUJO B)
  if (firstRightsFuture) {
    return {
      channel: firstRightsFuture.channel,
      rights: firstRightsFuture.rights,
      windowStatus: firstRightsFuture.windowStatus,
      showCommercialOffer: false,
      allChannels: channels,
    };
  }

  // Todos son NO_RIGHTS → oferta comercial con todos los canales (FLUJO C o D)
  if (allNoRights && firstNoRights) {
    return {
      channel: firstNoRights.channel,
      rights: CHANNEL_RIGHTS.NO_RIGHTS,
      windowStatus: firstNoRights.windowStatus,
      showCommercialOffer: true,
      allChannels: channels,
    };
  }

  return {
    channel: null,
    rights: null,
    windowStatus: null,
    showCommercialOffer: false,
    allChannels: channels,
  };
};

/**
 * Procesa los canales alternos (response del API v2 con playComments=true)
 * y devuelve el flujo que debe ejecutarse.
 *
 * @param {Array | null}  alternateChannels
 * @param {string | null} scheduledAt
 */
export const useAlternatePlaybackFlow = (alternateChannels, scheduledAt) => {

  return useMemo(() => {
    if (!alternateChannels || alternateChannels.length === 0) {
      return {
        channel: null,
        rights: null,
        windowStatus: null,
        showCommercialOffer: false,
        allChannels: [],
      };
    }
    return resolveActiveChannel(alternateChannels, scheduledAt);
  }, [alternateChannels, scheduledAt]);
};
